/**
 * Privacy Policy — public page (/privacy)
 *
 * Aligned with the Australian Privacy Act 1988 (Cth) and the Australian
 * Privacy Principles (APPs). Includes an automated-decision-making / AI
 * disclosure to align with the transparency obligations commencing
 * 10 December 2026. Placeholders (highlighted via <Ph>) must be replaced
 * with real details, and the document should be reviewed by a lawyer
 * before launch.
 */

import { LegalLayout, TOC, H2, P, UL, LI, Strong, Callout } from '../components/LegalLayout'

const SECTIONS = [
  { href: '#what', label: 'Information we collect' },
  { href: '#how', label: 'How we collect it' },
  { href: '#why', label: 'How we use it' },
  { href: '#ai', label: 'AI & automated processing' },
  { href: '#sharing', label: 'Who we share it with' },
  { href: '#overseas', label: 'Overseas disclosure' },
  { href: '#security', label: 'How we protect it' },
  { href: '#retention', label: 'How long we keep it' },
  { href: '#cookies', label: 'Cookies & analytics' },
  { href: '#rights', label: 'Your rights' },
  { href: '#complaints', label: 'Complaints' },
  { href: '#children', label: 'Children' },
  { href: '#changes', label: 'Changes' },
  { href: '#contact', label: 'Contact us' },
]

export default function Privacy() {
  return (
    <LegalLayout
      title="Privacy Policy"
      updated="13 October 2026"
      otherDoc={{ to: '/terms', label: 'Terms of Service' }}
    >
      <P>
        This Privacy Policy explains how JAI Fantasy Sports (ABN 62 279 215 204) (<Strong>"JAI"</Strong>,{' '}
        <Strong>"we"</Strong>, <Strong>"us"</Strong>, or <Strong>"our"</Strong>) collects, uses, discloses, and
        protects your personal information when you use JAI. We handle personal information in accordance with
        the <Strong>Privacy Act 1988 (Cth)</Strong> and the <Strong>Australian Privacy Principles (APPs)</Strong>.
      </P>

      <TOC items={SECTIONS} />

      <H2 id="what">1. Information we collect</H2>
      <UL>
        <LI>
          <Strong>Account information</Strong> — your first and last name, username, and email address.
        </LI>
        <LI>
          <Strong>Your password</Strong> — stored only as a securely hashed value. We cannot see or recover
          your actual password.
        </LI>
        <LI>
          <Strong>Fantasy league data</Strong> — when you connect a third-party platform (such as Sleeper,
          Yahoo, or ESPN), we collect data about your leagues, teams, rosters, scoring settings, and matchups
          through that platform.
        </LI>
        <LI>
          <Strong>Platform access credentials</Strong> — to read a private ESPN league, ESPN requires the
          two login values (<Strong>espn_s2</Strong> and <Strong>SWID</Strong>) your browser holds after you
          sign in to ESPN. You provide these either through the optional JAI browser extension or by entering
          them yourself. We store them solely to fetch your league on your behalf and never use them to
          change anything in your ESPN account. Removing the league from JAI erases them immediately, and
          you can also ask us to delete them at any time using the contact details below. Yahoo and Sleeper
          use their own sign-in methods and are not affected.
        </LI>
        <LI>
          <Strong>Live draft picks (ESPN)</Strong> — if you use the JAI browser extension (version 1.3.0 or
          later) while an ESPN draft is running, the extension reads a copy of the messages ESPN sends to
          your own draft room, on ESPN draft pages only. It passes the picks made so far (which team took
          which player) to your open JAI tab, and JAI saves that pick list against the league so Live Draft
          can show who has been taken and update your recommendations. The extension does not send anything
          to ESPN, removes your ESPN member ID and draft token from the connection address, and uploads
          nothing itself. So that a JAI tab you open part-way through a draft can catch up, the extension
          keeps the picks and state of the draft you are in, on your own device, in your browser's
          temporary session storage; it is cleared when you close the browser. The pick list JAI saves is
          replaced as the draft progresses, is cleared when the league moves to a new season, and is erased
          if you remove the league from JAI.
          Sleeper drafts are read from Sleeper's public draft feed and do not use the extension.
        </LI>
        <LI>
          <Strong>Content you create</Strong> — My Notes, saved research, and questions you ask JAI. My
          Notes are stored on JAI's servers (not just on one device), so you see the same notes wherever
          you sign in.
        </LI>
        <LI>
          <Strong>Usage and device data</Strong> — pages and features you use, approximate location, browser
          and device type, IP address, and log data, collected automatically.
        </LI>
        <LI>
          <Strong>Billing information</Strong> — handled by Stripe. We do not store full card numbers; we
          receive limited details such as your subscription status and the last four digits of your card.
        </LI>
        <LI>
          <Strong>Communications</Strong> — messages you send us, such as support requests.
        </LI>
      </UL>

      <H2 id="how">2. How we collect it</H2>
      <P>
        We collect personal information directly from you (when you sign up, fill in forms, or contact us),
        automatically (through cookies and similar technology as you use JAI), and from third-party fantasy
        platforms that you choose to connect to your account.
      </P>

      <H2 id="why">3. How we use it</H2>
      <P>We use your personal information to:</P>
      <UL>
        <LI>provide and personalise JAI, including generating rankings and recommendations for your specific leagues;</LI>
        <LI>create and secure your account, and authenticate you;</LI>
        <LI>process payments and manage your subscription;</LI>
        <LI>send you service messages, respond to support requests, and (with your consent) occasional product updates you can opt out of at any time;</LI>
        <LI>understand how JAI is used so we can fix problems and improve it; and</LI>
        <LI>meet our legal obligations.</LI>
      </UL>

      <H2 id="ai">4. AI &amp; automated processing</H2>
      <Callout tone="info">
        <P>
          To generate recommendations, JAI sends relevant information — such as your league settings, roster
          and player data, and the questions you ask — to our AI provider, <Strong>Anthropic</Strong> (the
          "Claude" AI), which produces fantasy analysis and suggestions.
        </P>
        <P>
          These are <Strong>automated suggestions to inform your decisions</Strong>. They do not make
          decisions that produce legal or similarly significant effects about you, and you always remain in
          control of your fantasy choices.
        </P>
      </Callout>

      <H2 id="sharing">5. Who we share it with</H2>
      <P>
        <Strong>We do not sell your personal information.</Strong> We share it only with service providers who
        help us run JAI, and only as needed to provide the service. These include:
      </P>
      <UL>
        <LI><Strong>Anthropic</Strong> — AI processing of your queries and league data;</LI>
        <LI><Strong>Stripe</Strong> — payment processing and subscription billing;</LI>
        <LI><Strong>Render</Strong> — application hosting and infrastructure;</LI>
        <LI><Strong>Resend</Strong> — sending account and service emails;</LI>
        <LI><Strong>Klaviyo</Strong> — sending emails about JAI itself (new features, plan changes, pricing and offers). Marketing emails go only to people who opt in; product-update emails go to account holders unless they opt out. Either can be turned off any time in Settings or with the unsubscribe link in any email, and we then share nothing further;</LI>
        <LI><Strong>PostHog</Strong> — product analytics, to understand how JAI is used;</LI>
        <LI><Strong>Sentry</Strong> — error tracking and diagnostics;</LI>
        <LI>the third-party fantasy platforms you connect, to retrieve your data at your direction.</LI>
      </UL>
      <P>
        We may also disclose personal information where required or authorised by law, or to protect the
        rights, safety, or property of JAI, our users, or others.
      </P>

      <H2 id="overseas">6. Overseas disclosure</H2>
      <P>
        Some of our service providers store or process data outside Australia, including in the{' '}
        <Strong>United States</Strong>. Where we disclose your personal information overseas, we take
        reasonable steps to ensure it is handled consistently with the Australian Privacy Principles. By using
        JAI, you acknowledge that your information may be processed outside Australia.
      </P>

      <H2 id="security">7. How we protect it</H2>
      <P>
        We take reasonable steps to protect your personal information. Passwords are stored using one-way
        hashing, data is encrypted in transit (HTTPS), and access to systems is restricted. No method of
        storage or transmission is completely secure, so we cannot guarantee absolute security.
      </P>
      <P>
        If an eligible data breach affecting your personal information occurs, we will respond in accordance
        with the <Strong>Notifiable Data Breaches scheme</Strong> and notify you and the Office of the
        Australian Information Commissioner (OAIC) where the scheme requires.
      </P>

      <H2 id="retention">8. How long we keep it</H2>
      <P>
        We keep your personal information only for as long as we need it for the purposes described in this
        policy, or for as long as the law requires. When you delete your account, we delete or de-identify your
        personal information within a reasonable period, except where we must retain certain records (for
        example, for tax or legal reasons).
      </P>

      <H2 id="cookies">9. Cookies &amp; analytics</H2>
      <P>
        We use cookies and similar technologies to keep you signed in, remember your preferences, and
        understand how JAI is used. You can control or disable cookies through your browser settings, though
        some parts of JAI may not work properly without them.
      </P>
      <P>
        For analytics we use <Strong>PostHog</Strong>, which sets cookies to measure things like which
        pages are visited and which features are used. We identify you to PostHog by your account ID
        only &mdash; not your name or email address &mdash; and we have configured it not to receive the
        links in your account emails, such as password-reset links.
      </P>
      <P>
        PostHog also records <Strong>session replays</Strong>: a reconstruction of how you moved through
        the app, which we use to find where JAI is confusing or broken. Everything you type into a form
        &mdash; passwords, email addresses, notes &mdash; is masked before it leaves your browser and is
        never recorded. You can stop replays being recorded by turning on{' '}
        <Strong>Do Not Track</Strong> in your browser settings, and you can ask us to delete recordings
        of your sessions using the contact details below.
      </P>

      <H2 id="rights">10. Your rights</H2>
      <P>Under the Australian Privacy Principles, you can:</P>
      <UL>
        <LI><Strong>access</Strong> the personal information we hold about you;</LI>
        <LI>ask us to <Strong>correct</Strong> information that is inaccurate or out of date;</LI>
        <LI><Strong>withdraw consent</Strong> or opt out of marketing communications; and</LI>
        <LI>ask us to <Strong>delete</Strong> your account and personal information.</LI>
      </UL>
      <P>
        To make a request, email us at jai@play-jai.com. We may need to verify your identity first,
        and we will respond within a reasonable time.
      </P>

      <H2 id="complaints">11. Complaints</H2>
      <P>
        If you have a concern about how we handle your personal information, please contact us first at{' '}
        jai@play-jai.com so we can try to resolve it. If you are not satisfied with our response, you
        can lodge a complaint with the Office of the Australian Information Commissioner (OAIC) at{' '}
        <Strong>oaic.gov.au</Strong> or by calling 1300 363 992.
      </P>

      <H2 id="children">12. Children</H2>
      <P>
        JAI is intended for people aged 18 and over. We do not knowingly collect personal information from
        anyone under 18. If you believe a child has provided us with personal information, please contact us
        and we will delete it.
      </P>

      <H2 id="changes">13. Changes to this policy</H2>
      <P>
        We may update this Privacy Policy from time to time. If we make a material change, we will update the
        "Last updated" date above and take reasonable steps to notify you.
      </P>

      <H2 id="contact">14. Contact us</H2>
      <P>
        For any privacy question or request, contact JAI Fantasy Sports at jai@play-jai.com,
        ABN 62 279 215 204, Victoria, Australia.
      </P>
    </LegalLayout>
  )
}
